import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { ADFResponse } from '../../ai-session-context/types';

type RawADFNode = {
	attrs?: Record<string, unknown>;
	content?: RawADFNode[];
	type: string;
};

function findMatchingOrOnlyCell<T extends { attrs?: Record<string, unknown> }>(
	matches: T[],
	originalLocalId: unknown,
): T | undefined {
	const matchingId = matches.filter((node) => node.attrs?.localId === originalLocalId);
	if (matchingId.length === 1) {
		return matchingId[0];
	}
	return matchingId.length === 0 && matches.length === 1 ? matches[0] : undefined;
}

function findReplacementCell(content: Fragment, originalCell: PMNode): PMNode | undefined {
	const firstChild = content.firstChild;
	if (content.childCount === 1 && firstChild?.type === originalCell.type) {
		return firstChild;
	}

	const matches: PMNode[] = [];
	content.forEach((node) => {
		node.descendants((child) => {
			if (child.type === originalCell.type) {
				matches.push(child);
			}
		});
	});
	return findMatchingOrOnlyCell(matches, originalCell.attrs.localId);
}

function findRawCell(
	response: RawADFNode | undefined,
	originalCell: PMNode,
): RawADFNode | undefined {
	if (!response) {
		return undefined;
	}
	const directNode = response.type === 'doc' ? response.content?.[0] : response;
	if (directNode?.type === originalCell.type.name) {
		return directNode;
	}

	const matches: RawADFNode[] = [];
	const visit = (node: RawADFNode): void => {
		if (node.type === originalCell.type.name) {
			matches.push(node);
		}
		node.content?.forEach(visit);
	};
	visit(response);
	return findMatchingOrOnlyCell(matches, originalCell.attrs.localId);
}

/** Restore explicit cell attributes when replaceRange merges into the selected cell wrapper. */
export function applyCellReplacementAttributes({
	tr,
	originalDoc,
	replacementContent,
	adfResponse,
	startPos,
	endPos,
}: {
	adfResponse: ADFResponse | undefined;
	endPos: number;
	originalDoc: PMNode;
	replacementContent: Fragment;
	startPos: number;
	tr: Transaction;
}): Transaction {
	if (startPos < 0 || startPos > originalDoc.content.size) {
		return tr;
	}

	const $start = originalDoc.resolve(startPos);
	const cellDepth = Array.from({ length: $start.depth }, (_, index) => $start.depth - index).find(
		(depth) =>
			$start.node(depth).type.name === 'tableCell' ||
			$start.node(depth).type.name === 'tableHeader',
	);
	if (cellDepth === undefined) {
		return tr;
	}

	const cellPos = $start.before(cellDepth);
	const originalCell = $start.node(cellDepth);
	if (startPos !== cellPos + 1 || endPos !== cellPos + originalCell.nodeSize - 1) {
		return tr;
	}
	if (!isExperimentEnabled('platform_editor_ai_replace_cell_attrs_exp')) {
		return tr;
	}
	const replacement = findReplacementCell(replacementContent, originalCell);
	const rawNode = findRawCell(adfResponse as RawADFNode | undefined, originalCell);
	if (!replacement || !rawNode?.attrs) {
		return tr;
	}

	const currentCell = tr.doc.nodeAt(cellPos);
	if (currentCell?.type !== originalCell.type) {
		return tr;
	}

	const attrs = { ...currentCell.attrs };
	let changed = false;
	for (const name of ['background', 'valign'] as const) {
		if (!(name in rawNode.attrs) || !(name in replacement.attrs)) {
			continue;
		}
		const value = replacement.attrs[name];
		if (attrs[name] !== value) {
			attrs[name] = value;
			changed = true;
		}
	}

	return changed ? tr.setNodeMarkup(cellPos, undefined, attrs, currentCell.marks) : tr;
}
